import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { AuditLogDetailPanel } from '@/features/AuditLogs/components/AuditLogDetailPanel/AuditLogDetailPanel'
import { AuditLogsFilters } from '@/features/AuditLogs/components/AuditLogsFilters/AuditLogsFilters'
import { AuditLogsIncompleteNotice } from '@/features/AuditLogs/components/AuditLogsIncompleteNotice/AuditLogsIncompleteNotice'
import { AuditLogsLoadMore } from '@/features/AuditLogs/components/AuditLogsLoadMore/AuditLogsLoadMore'
import {
  AUDIT_LOGS_SKELETON_WIDTHS,
  AuditLogsTable,
} from '@/features/AuditLogs/components/AuditLogsTable/AuditLogsTable'
import {
  type DateRangePreset,
  useAuditLogFilters,
} from '@/features/AuditLogs/hooks/useAuditLogFilters'
import { useMergedAuditLogs } from '@/features/AuditLogs/hooks/useMergedAuditLogs'
import {
  AUDIT_LOG_STATUSES,
  type AuditLogStatus,
} from '@/features/AuditLogs/lib/auditSourceParams'
import type { AuditLogs } from '@/generated/http-clients/v3ApiSchemas'
import {
  ERROR_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
  toaster,
} from '@/hooks/useToast/toastUtils'
import { auditLogsRootRoute } from '@/router/rootRoutes'
import {
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Icon,
  Separator,
  Splitter,
} from '@chakra-ui/react'
import { createRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { Link2, RefreshCw } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const auditLogsSearchParams = z.object({
  dateRange: z
    .enum(['lastHour', 'last24Hours', 'last7Days', 'last30Days', 'allTime'])
    .optional()
    .default('last24Hours'),
  hiveId: z.string().optional(),
  orgId: z.string().optional(),
  user: z.string().optional(),
  status: z.enum(AUDIT_LOG_STATUSES).optional(),
  licenseId: z.string().optional(),
  userId: z.string().optional(),
})

export type AuditLogsSearchParams = z.infer<typeof auditLogsSearchParams>

export const auditLogsRoute = createRoute({
  getParentRoute: () => auditLogsRootRoute,
  path: '/',
  component: AuditLogsPage,
  validateSearch: auditLogsSearchParams,
})

const SPLITTER_COLLAPSED = [100, 0] as const
const SPLITTER_EXPANDED = [65, 35] as const

function AuditLogsPage() {
  const { t } = useTranslation('features', { useSuspense: false })
  const navigate = useNavigate()
  const search = useSearch({ from: auditLogsRoute.id })

  const [selectedLog, setSelectedLog] = useState<AuditLogs | null>(null)
  const [rangeEnd, setRangeEnd] = useState(() => Date.now())

  const dateRangePreset = (search.dateRange ?? 'last24Hours') as DateRangePreset
  const hiveId = search.hiveId ?? null
  const orgId = search.orgId ?? null
  const user = search.user ?? null
  const statusFilter = search.status ?? null
  const licenseId = search.licenseId ?? null
  const userId = search.userId ?? null

  const { enhancedQueryParams, hasActiveFilters } = useAuditLogFilters({
    rangeEnd,
    dateRangePreset,
    hiveId,
    orgId,
    user,
    status: statusFilter,
    licenseId,
    userId,
  })

  const mergedLogs = useMergedAuditLogs(enhancedQueryParams)

  const updateSearchParams = (updates: Partial<AuditLogsSearchParams>) => {
    setRangeEnd(Date.now())
    navigate({
      to: auditLogsRoute.to,
      search: (prev) => ({ ...prev, ...updates }),
      replace: true,
    })
  }

  const handleDateRangeChange = (preset: DateRangePreset) => {
    updateSearchParams({ dateRange: preset })
  }

  const handleHiveIdChange = (value: string | null) => {
    updateSearchParams({ hiveId: value ?? undefined })
  }

  const handleOrgIdChange = (value: string | null) => {
    updateSearchParams({ orgId: value ?? undefined })
  }

  const handleUserChange = (value: string | null) => {
    updateSearchParams({ user: value ?? undefined })
  }

  const handleStatusChange = (value: AuditLogStatus | null) => {
    updateSearchParams({ status: value ?? undefined })
  }

  const handleLicenseIdChange = (value: string | null) => {
    updateSearchParams({ licenseId: value ?? undefined })
  }

  const handleUserIdChange = (value: string | null) => {
    updateSearchParams({ userId: value ?? undefined })
  }

  const handleClearFilters = () => {
    setRangeEnd(Date.now())
    navigate({
      to: auditLogsRoute.to,
      search: { dateRange: 'last24Hours' },
      replace: true,
    })
  }

  const handleRefresh = () => {
    setRangeEnd(Date.now())
    mergedLogs.refresh()
  }

  const handleCopyUrl = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        toaster.create({
          ...SUCCESS_TOAST_OPTION,
          title: String(t('auditLogs.copyUrl.success')),
        })
      })
      .catch(() => {
        toaster.create({
          ...ERROR_TOAST_OPTION,
          title: String(t('auditLogs.copyUrl.error')),
        })
      })
  }

  const [splitterSizes, setSplitterSizes] = useState<number[]>([
    ...SPLITTER_COLLAPSED,
  ])
  const isDetailsPanelOpen = splitterSizes[1] > 0

  const handleRowClick = useCallback((log: AuditLogs) => {
    setSelectedLog(log)
    setSplitterSizes((prev) => (prev[1] === 0 ? [...SPLITTER_EXPANDED] : prev))
  }, [])

  const handleCollapseDetails = useCallback(() => {
    setSplitterSizes([...SPLITTER_COLLAPSED])
  }, [])

  return (
    <PageLayout
      title={t('auditLogs.title')}
      action={
        <HStack gap={4}>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyUrl}
            colorPalette="gray"
            aria-label={String(t('auditLogs.copyUrl.button'))}
          >
            <Icon as={Link2} />
            {t('auditLogs.copyUrl.button')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            colorPalette="gray"
            onClick={handleRefresh}
          >
            <Icon as={RefreshCw} />
            {t('auditLogs.refresh')}
          </Button>
        </HStack>
      }
    >
      <Flex direction="column" h="100%" w="100%">
        <Box mb={4}>
          <AuditLogsFilters
            dateRangePreset={dateRangePreset}
            hiveId={hiveId}
            orgId={orgId}
            user={user}
            status={statusFilter}
            licenseId={licenseId}
            userId={userId}
            hasActiveFilters={hasActiveFilters}
            onDateRangeChange={handleDateRangeChange}
            onHiveIdChange={handleHiveIdChange}
            onOrgIdChange={handleOrgIdChange}
            onUserChange={handleUserChange}
            onStatusChange={handleStatusChange}
            onLicenseIdChange={handleLicenseIdChange}
            onUserIdChange={handleUserIdChange}
            onClearFilters={handleClearFilters}
          />
        </Box>

        <Separator />

        <Splitter.Root
          size={splitterSizes}
          onResize={(details) => setSplitterSizes(details.size)}
          panels={[
            { id: 'table', minSize: 40 },
            { id: 'details', collapsible: true, collapsedSize: 0, minSize: 20 },
          ]}
          flex="1"
          minH="500px"
          borderTopWidth="0"
        >
          <Splitter.Panel id="table">
            <Box
              h="100%"
              display="flex"
              flexDirection="column"
              pt={4}
              pr={isDetailsPanelOpen ? 4 : 0}
            >
              {mergedLogs.isIncomplete && <AuditLogsIncompleteNotice />}
              <Card.Root flex="1" overflow="auto">
                <AuditLogsTable
                  logs={mergedLogs.entries}
                  isLoading={mergedLogs.isLoading}
                  isError={mergedLogs.isError}
                  isEmpty={mergedLogs.isEmpty}
                  skeletonWidths={AUDIT_LOGS_SKELETON_WIDTHS}
                  selectedLogId={selectedLog?.id}
                  onRowClick={handleRowClick}
                  errorComponent={
                    <ApiErrorState
                      apiResource={mergedLogs.errorResource}
                      errorTitle={t('auditLogs.errors.title')}
                      errorDescription={t('auditLogs.errors.description')}
                    />
                  }
                  emptyMessage={t('auditLogs.table.noResults')}
                />
              </Card.Root>

              {mergedLogs.hasMore && mergedLogs.entries.length > 0 && (
                <AuditLogsLoadMore
                  isLoading={mergedLogs.isFetchingMore}
                  onLoadMore={mergedLogs.loadMore}
                />
              )}
            </Box>
          </Splitter.Panel>

          {isDetailsPanelOpen && <Splitter.ResizeTrigger id="table:details" />}

          <Splitter.Panel id="details">
            <Box
              h="100%"
              overflow="hidden"
              bg="white"
              borderLeftWidth={isDetailsPanelOpen ? '1px' : '0'}
            >
              <AuditLogDetailPanel
                log={selectedLog}
                onCollapse={handleCollapseDetails}
              />
            </Box>
          </Splitter.Panel>
        </Splitter.Root>
      </Flex>
    </PageLayout>
  )
}
